import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Lock } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { settingsStore, LanguageType } from '@/stores/settingsStore'
import { userStore } from '@/stores/userStore'
import { useTranslation } from 'react-i18next'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api'
import { CookiePreferencesLink } from '@/components/Consent/CookiePreferencesLink'

interface ModuleOption {
  id: number
  name: string
}

const GET_USER_MODULES = gql`
  query getUserModules {
    getUserModules {
      id
      name
    }
  }
`

const PreferencesTab: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [language, setLanguage] = useState<LanguageType>(settingsStore.language)
  const [timezone, setTimezone] = useState('UTC')
  const [autoDelete, setAutoDelete] = useState('month')
  const [georgiaVoiceEnabled, setGeorgiaVoiceEnabled] = useState(settingsStore.georgiaVoiceEnabled)
  const [roleplayVoiceEnabled, setRoleplayVoiceEnabled] = useState(settingsStore.roleplayVoiceEnabled)
  const [module, setModule] = useState<number | null>(settingsStore.module)
  const [availableModules, setAvailableModules] = useState<ModuleOption[]>([])
  const [isLoadingModules, setIsLoadingModules] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch user-assigned modules on mount
  useEffect(() => {
    const fetchModules = async () => {
      try {
        setIsLoadingModules(true)
        const data = await authorizedGraphQLRequest<{ getUserModules: ModuleOption[] }>(GET_USER_MODULES)
        const modules = data.getUserModules || []
        setAvailableModules(modules)

        // Auto-select if only one module and no preference saved
        if (modules.length > 0 && settingsStore.module === null) {
          setModule(modules[0].id)
        } else if (settingsStore.module !== null) {
          setModule(settingsStore.module)
        }
      } catch (err) {
        console.error('Failed to fetch user modules:', err)
      } finally {
        setIsLoadingModules(false)
      }
    }
    fetchModules()
  }, [])

  // Sync with store when it changes
  // useEffect(() => {
  //   setLanguage(settingsStore.language)
  // }, [settingsStore.language])

  // Update i18n language when the language state changes
  // useEffect(() => {
  //   i18n.changeLanguage(language)
  // }, [language, i18n])

  const handleLanguageChange = (value: string) => {
    setLanguage(value as LanguageType)
  }

  return (
    <Card className="border-none p-0 shadow-none mt-4">
      <CardHeader className="p-0 mb-6">
        <CardTitle className="text-2xl font-bold text-gray-800">{t('settings.title')}</CardTitle>
        <CardDescription className="text-gray-600 mt-2">{t('settings.manage_preferences')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-8 p-0">
        <div className="space-y-5">
          <div className="text-xl font-bold text-gray-800">{t('settings.language_region')}</div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-3">
              <Label htmlFor="language" className="text-sm font-semibold text-gray-700">
                {t('settings.language')}
              </Label>
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="border-gray-200 bg-gray-50 focus:ring-primary focus:border-primary w-full h-12 rounded-lg shadow-sm font-medium">
                  <SelectValue placeholder={t('settings.select_language')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label htmlFor="timezone" className="text-sm font-semibold text-gray-700">
                {t('settings.timezone')}
              </Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger className="border-gray-200 bg-gray-50 focus:ring-primary focus:border-primary w-full h-12 rounded-lg shadow-sm font-medium">
                  <SelectValue placeholder={t('settings.select_timezone')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">UTC</SelectItem>
                  <SelectItem value="EST">Eastern Time (EST)</SelectItem>
                  <SelectItem value="CST">Central Time (CST)</SelectItem>
                  <SelectItem value="PST">Pacific Time (PST)</SelectItem>
                  <SelectItem value="CET">Central European Time (CET)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* <Separator />

        <div className="space-y-5">
          <div className="text-xl font-bold text-gray-800">{t('settings.privacy_settings')}</div>
          <div className="space-y-4 p-6 bg-gradient-to-r from-gray-50 to-white rounded-xl shadow-md">
            <div className="flex items-center justify-between p-4 bg-white rounded-lg hover:shadow-sm transition-all duration-200">
              <div className="space-y-1">
                <Label htmlFor="analytics" className="font-semibold text-gray-800">
                  {t('settings.usage_analytics')}
                </Label>
                <div className="text-sm text-gray-600">{t('settings.allow_analytics')}</div>
              </div>
              <Switch id="analytics" defaultChecked className="data-[state=checked]:bg-primary" />
            </div>
            <div className="flex items-center justify-between p-4 bg-white rounded-lg hover:shadow-sm transition-all duration-200">
              <div className="space-y-1">
                <Label htmlFor="marketing" className="font-semibold text-gray-800">
                  {t('settings.marketing')}
                </Label>
                <div className="text-sm text-gray-600">{t('settings.receive_updates')}</div>
              </div>
              <Switch id="marketing" defaultChecked className="data-[state=checked]:bg-primary" />
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors cursor-pointer group">
            <Lock className="h-4 w-4 text-primary group-hover:scale-110 transition-transform" />
            <span className="text-sm text-primary font-semibold group-hover:text-primary/90">{t('settings.view_privacy')}</span>
          </div>
        </div> */}

        <Separator />

        <div className="space-y-5">
          <div className="text-xl font-bold text-gray-800">{t('settings.privacy_settings')}</div>
          <div className="p-6 bg-gradient-to-r from-gray-50 to-white rounded-xl shadow-md">
            <div className="text-sm text-gray-600">{t('consent.settings_hint')}</div>
            <CookiePreferencesLink className="mt-3 font-semibold text-primary" />
          </div>
        </div>

        <Separator />

        {/* <div className="space-y-5">
          <div className="text-xl font-bold text-gray-800">{t('settings.autodelete')}</div>
          <RadioGroup value={autoDelete} onValueChange={setAutoDelete} className="space-y-3 [&_svg.fill-primary]:fill-primary p-6 bg-gradient-to-r from-gray-50 to-white rounded-xl shadow-md">
            <div className="flex items-center space-x-3 p-4 bg-white rounded-lg hover:shadow-sm transition-all duration-200 cursor-pointer">
              <RadioGroupItem value="week" id="week" className="text-primary border-gray-300 data-[state=checked]:border-primary data-[state=checked]:border-2 data-[state=checked]:bg-white" />
              <Label htmlFor="week" className="font-medium text-gray-700 cursor-pointer flex-1">
                {t('settings.delete_week')}
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-4 bg-white rounded-lg hover:shadow-sm transition-all duration-200 cursor-pointer">
              <RadioGroupItem value="month" id="month" className="text-primary border-gray-300 data-[state=checked]:border-primary data-[state=checked]:border-2 data-[state=checked]:bg-white" />
              <Label htmlFor="month" className="font-medium text-gray-700 cursor-pointer flex-1">
                {t('settings.delete_month')}
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-4 bg-white rounded-lg hover:shadow-sm transition-all duration-200 cursor-pointer">
              <RadioGroupItem value="never" id="never" className="text-primary border-gray-300 data-[state=checked]:border-primary data-[state=checked]:border-2 data-[state=checked]:bg-white" />
              <Label htmlFor="never" className="font-medium text-gray-700 cursor-pointer flex-1">
                {t('settings.never_delete')}
              </Label>
            </div>
          </RadioGroup>
        </div> */}

        {/* <Separator /> */}

        <div className="space-y-5">
          <div className="text-xl font-bold text-gray-800">Module</div>
          {isLoadingModules ? (
            <div className="p-6 bg-gradient-to-r from-gray-50 to-white rounded-xl shadow-md">
              <div className="text-gray-400 text-sm">Loading modules...</div>
            </div>
          ) : availableModules.length === 0 ? (
            <div className="p-6 bg-gradient-to-r from-gray-50 to-white rounded-xl shadow-md">
              <div className="text-gray-500 text-sm">No modules assigned to your account.</div>
            </div>
          ) : (
            <RadioGroup
              value={module !== null ? String(module) : ''}
              onValueChange={(value) => setModule(parseInt(value, 10))}
              className="space-y-3 [&_svg.fill-primary]:fill-primary p-6 bg-gradient-to-r from-gray-50 to-white rounded-xl shadow-md"
            >
              {availableModules.map((mod) => (
                <div key={mod.id} className="flex items-center space-x-3 p-4 bg-white rounded-lg hover:shadow-sm transition-all duration-200 cursor-pointer">
                  <RadioGroupItem
                    value={String(mod.id)}
                    id={`module-${mod.id}`}
                    className="text-primary border-gray-300 data-[state=checked]:border-primary data-[state=checked]:border-2 data-[state=checked]:bg-white"
                  />
                  <Label htmlFor={`module-${mod.id}`} className="font-medium text-gray-700 cursor-pointer flex-1">
                    {mod.name}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}
        </div>

        <Separator />

        {/* <div className="space-y-4">
          <div className="text-lg font-semibold">{t('settings.chat_mode')}</div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="georgiaVoice">{t('settings.georgia_voice')}</Label>
                <div className="text-sm text-muted-foreground">{t('settings.georgia_voice_description')}</div>
              </div>
              <Switch id="georgiaVoice" checked={georgiaVoiceEnabled} onCheckedChange={setGeorgiaVoiceEnabled} className="data-[state=checked]:bg-primary" />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="roleplayVoice">{t('settings.roleplay_voice')}</Label>
                <div className="text-sm text-muted-foreground">{t('settings.roleplay_voice_description')}</div>
              </div>
              <Switch id="roleplayVoice" checked={roleplayVoiceEnabled} onCheckedChange={setRoleplayVoiceEnabled} className="data-[state=checked]:bg-primary" />
            </div>
          </div>
        </div> */}
      </CardContent>
      <CardFooter className="space-y-3 pt-6">
        {error && <div className="text-red-500 text-sm font-semibold bg-red-50 p-3 rounded-lg">{error}</div>}
        <Button
          className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 h-12 px-8 rounded-xl"
          onClick={async () => {
            try {
              setIsSaving(true)
              setError(null)

              // Update store with current values
              settingsStore.setLanguage(language)
              settingsStore.setGeorgiaVoiceEnabled(georgiaVoiceEnabled)
              settingsStore.setRoleplayVoiceEnabled(roleplayVoiceEnabled)
              settingsStore.setModule(module)

              // Save settings
              await settingsStore.saveLLMSettings()
            } catch (err) {
              console.error('Failed to save preferences:', err)
              setError(t('settings.save_error'))
            } finally {
              setIsSaving(false)
            }
          }}
          disabled={isSaving}
        >
          {isSaving ? t('common.loading') : t('common.save')}
        </Button>
      </CardFooter>
    </Card>
  )
}

export default observer(PreferencesTab)
